<!--
  DeliveryPanel — F2 (GitLab, pipelines y entrega) / U2.10: panel
  INTERNO (platform_admin + tenant_operator). El rol tenant_client no ve
  esto — ve la señal de negocio "estabilidad de los cambios" en
  PortalView, sin shas, ramas ni pipelines.

  Cuatro bloques, todos ya resueltos por el backend
  (GET /panels/delivery): resumen de pipelines de la ventana con tasa de
  fallo, último despliegue por entorno, commits por módulo, y
  correlación "este grupo de errores apareció después de este
  despliegue" (U2.7c) — el mismo tipo de correlación que ErrorGroupsPanel
  hace con entornos, pero acá con el despliegue inmediatamente anterior.
-->

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import Icon from '@/components/ds/Icon.vue';
import BarRankRow from '@/components/panels/BarRankRow.vue';
import ListPager from '@/components/panels/ListPager.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { useListPage } from '@/composables/useListPage.js';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { DELIVERY_EMPTY } from '@/utils/emptyStateCopy.js';

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');

const pipelines = ref({ total: 0, failed: 0, ratio: null });
const deploymentsByEnvironment = ref([]);
const commitsByModule = ref([]);
const errorCorrelation = ref([]);
const deploymentList = useListPage(deploymentsByEnvironment, 5);
const correlationList = useListPage(errorCorrelation, 5);
const moduleList = useListPage(commitsByModule, 5);
const totalCommits = computed(() => commitsByModule.value.reduce((total, m) => total + m.commits, 0));

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  const r = await api.events.delivery({ appId: activeAppId.value, window: timeWindow.value, signal });
  if (!r || r.ok !== true) throw new Error('Invalid delivery response');
  return r;
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    pipelines.value = result.pipelines ?? { total: 0, failed: 0, ratio: null };
    deploymentsByEnvironment.value = result.deploymentsByEnvironment ?? [];
    commitsByModule.value = result.commitsByModule ?? [];
    errorCorrelation.value = result.errorCorrelation ?? [];
    const empty = pipelines.value.total === 0
      && deploymentsByEnvironment.value.length === 0
      && commitsByModule.value.every((m) => m.commits === 0)
      && errorCorrelation.value.length === 0;
    ps.setState(empty ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load delivery data",
      detail: err?.message ?? ''
    });
  }
});

watch(() => tenant.changeKey, () => {
  pipelines.value = { total: 0, failed: 0, ratio: null };
  deploymentsByEnvironment.value = [];
  commitsByModule.value = [];
  errorCorrelation.value = [];
  deploymentList.page.value = 1;
  correlationList.page.value = 1;
  moduleList.page.value = 1;
  ps.setState('loading');
});

ps.setRetryHandler(() => tick());
defineExpose({ ps, refreshStatus });

function formatRatio(ratio) {
  if (ratio === null || ratio === undefined) return '—';
  return `${ratio.toFixed(2)}%`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function shortSha(sha) {
  return sha ? sha.slice(0, 8) : '—';
}
</script>

<template>
  <PanelCard
    title="Delivery"
    :state="ps.state.value"
    :empty-headline="DELIVERY_EMPTY.headline"
    :empty-body="DELIVERY_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="delivery">
      <p class="delivery__hint">
        Delivery activity and the errors first observed after a deployment.
      </p>
      <div class="delivery__overview">
        <AnalyticsChart
          v-if="pipelines.total > 0"
          type="doughnut"
          :labels="['Failed', 'Other outcomes']"
          :datasets="[{ label: 'Pipelines', data: [pipelines.failed, Math.max(0, pipelines.total - pipelines.failed)], backgroundColor: ['var(--danger-icon)', 'var(--chart-1)'] }]"
          :aria-label="`${formatRatio(pipelines.ratio)} failure rate; ${pipelines.failed} failed of ${pipelines.total} pipelines`"
          percentage
          :percentage-total="pipelines.total"
          :center-value="formatRatio(pipelines.ratio)"
          center-label="failure rate"
          class="delivery__chart"
        />
        <dl class="delivery__summary">
          <div>
            <dt>Pipelines in window</dt>
            <dd data-test="delivery-pipelines-total">
              {{ pipelines.total }}
            </dd>
          </div>
          <div>
            <dt>Failure rate</dt>
            <dd data-test="delivery-pipelines-ratio">
              {{ formatRatio(pipelines.ratio) }}
            </dd>
            <p class="delivery__hint">
              {{ pipelines.failed }} failed of {{ pipelines.total }}
            </p>
          </div>
          <div>
            <dt>Module commits</dt>
            <dd>{{ totalCommits.toLocaleString() }}</dd>
            <p class="delivery__hint">
              {{ commitsByModule.length }} modules with recorded activity
            </p>
          </div>
        </dl>
      </div>

      <div class="delivery__columns">
        <section class="delivery__section">
          <h3 class="delivery__h3">
            Latest deployment per environment
          </h3>
          <ul
            v-if="deploymentsByEnvironment.length"
            class="delivery__deployments"
          >
            <li
              v-for="d in deploymentList.visible.value"
              :key="`${tenant.changeKey}-${deploymentList.page.value}-${d.environment}`"
              data-test="delivery-deployment-row"
            >
              <details class="delivery__disclosure">
                <summary><Icon name="branch" /><span class="delivery__row-copy"><span class="delivery__env">{{ d.environment }}</span><span class="delivery__hint">{{ d.branch ?? '—' }} · {{ formatDate(d.deployedAt) }}</span></span><Icon name="chevDown" /></summary>
                <dl class="delivery__detail">
                  <div><dt>Release</dt><dd>{{ d.sha || '—' }}</dd></div>
                  <div><dt>Pipeline</dt><dd>{{ d.pipeline || '—' }}</dd></div>
                  <div><dt>Deployed by</dt><dd>{{ d.deployedBy || '—' }}</dd></div>
                  <div><dt>Tag</dt><dd>{{ d.tag || '—' }}</dd></div>
                </dl>
              </details>
            </li>
          </ul>
          <p
            v-else
            class="delivery__hint"
          >
            No deployments recorded yet.
          </p>
          <ListPager
            v-model:page="deploymentList.page.value"
            :total="deploymentsByEnvironment.length"
            :page-size="5"
            label="Deployments pagination"
          />
        </section>

        <section class="delivery__section">
          <h3 class="delivery__h3">
            Commits per module
          </h3>
          <ul
            v-if="commitsByModule.length"
            class="delivery__modules"
          >
            <li
              v-for="m in moduleList.visible.value"
              :key="m.moduleKey"
              data-test="delivery-module-row"
            >
              <BarRankRow
                :label="m.moduleKey"
                :count="m.commits"
                :pct="totalCommits ? m.commits / totalCommits * 100 : null"
              />
            </li>
          </ul>
          <p
            v-else
            class="delivery__hint"
          >
            No commits attributed to a module in this window.
          </p>
          <ListPager
            v-model:page="moduleList.page.value"
            :total="commitsByModule.length"
            :page-size="5"
            label="Module commits pagination"
          />
        </section>
      </div>

      <section class="delivery__section">
        <h3 class="delivery__h3">
          Errors after deployment
        </h3>
        <p class="delivery__hint">
          Temporal association only; a preceding deployment is not a confirmed cause. Occurrences are lifetime totals.
        </p>
        <ul
          v-if="errorCorrelation.length"
          class="delivery__correlation"
        >
          <li
            v-for="e in correlationList.visible.value"
            :key="`${tenant.changeKey}-${correlationList.page.value}-${e.fingerprint}`"
            data-test="delivery-correlation-row"
          >
            <details class="delivery__disclosure">
              <summary><Icon name="alert" /><span class="delivery__row-copy"><span>{{ e.sampleMessage || e.moduleKey }}</span><span class="delivery__hint">{{ e.moduleKey }} · {{ e.deployment?.environment || 'No prior deployment' }}</span></span><span class="delivery__occurrences">{{ e.occurrences }}×</span><Icon name="chevDown" /></summary>
              <div class="delivery__detail">
                <p>{{ e.sampleMessage }}</p>
                <span
                  v-if="e.deployment"
                  class="delivery__hint"
                >
                  after {{ shortSha(e.deployment.sha) }} ({{ e.deployment.environment }},
                  {{ formatDate(e.deployment.deployedAt) }})
                </span>
                <span
                  v-else
                  class="delivery__hint"
                >
                  no prior deployment on record
                </span>
                <p class="delivery__hint">
                  First seen: {{ formatDate(e.firstSeenAt) }} · Last seen: {{ formatDate(e.lastSeenAt) }}
                </p>
                <p class="delivery__hint">
                  Status: {{ e.status || '—' }} · Fingerprint: {{ e.fingerprint }}
                </p>
              </div>
            </details>
          </li>
        </ul>
        <p
          v-else
          class="delivery__hint"
        >
          No error groups in this window.
        </p>
        <ListPager
          v-model:page="correlationList.page.value"
          :total="errorCorrelation.length"
          :page-size="5"
          label="Deployment errors pagination"
        />
      </section>
    </div>

    <template #tableFallback>
      <table class="delivery__table">
        <caption class="visually-hidden">
          Latest deployment per environment
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Environment
            </th>
            <th scope="col">
              Sha
            </th>
            <th scope="col">
              Deployed at
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="d in deploymentList.visible.value"
            :key="`table-${d.environment}`"
          >
            <td>{{ d.environment }}</td>
            <td>{{ shortSha(d.sha) }}</td>
            <td>{{ formatDate(d.deployedAt) }}</td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.delivery {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.delivery__summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-6);
  margin: 0;
  flex: 1;
  min-width: 0;
}

.delivery__summary dt {
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.delivery__summary dd {
  margin: var(--space-1) 0 0;
  color: var(--fg-primary);
  font-size: var(--text-3xl);
  font-feature-settings: "tnum";
}

.delivery__hint {
  color: var(--fg-tertiary);
  font-size: var(--text-xs);
  margin: var(--space-1) 0 0;
}

.delivery__section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
}

.delivery__h3 {
  font-size: var(--text-sm);
  color: var(--fg-secondary);
  margin: 0;
}

.delivery__deployments,
.delivery__modules,
.delivery__correlation {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--space-2);
}

.delivery__deployments li,
.delivery__correlation li {
  font-size: var(--text-sm);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
}

.delivery__env {
  font-weight: var(--fw-semibold);
  color: var(--fg-primary);
  text-transform: capitalize;
}

.delivery__overview {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  padding: var(--space-5);
  background: var(--bg-subtle);
  border-radius: var(--radius-xl);
}

.delivery__chart {
  flex: 0 0 10rem;
  max-width: 10rem;
}

.delivery__columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
}

.delivery__disclosure summary {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  cursor: pointer;
  list-style: none;
}

.delivery__disclosure summary::-webkit-details-marker {
  display: none;
}

.delivery__disclosure summary:hover {
  background: var(--bg-subtle);
}

.delivery__disclosure summary:focus-visible {
  outline: var(--bw-thick) solid var(--accent-border);
  outline-offset: var(--space-1);
}

.delivery__disclosure summary>svg {
  flex-shrink: 0;
}

.delivery__disclosure[open] summary>svg:last-child {
  transform: rotate(180deg);
}

.delivery__row-copy {
  display: grid;
  flex: 1;
  min-width: 0;
  gap: var(--space-1);
}

.delivery__row-copy>span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--fw-medium);
}

.delivery__row-copy .delivery__hint {
  overflow-wrap: anywhere;
}

.delivery__occurrences {
  font-variant-numeric: tabular-nums;
}

.delivery__detail {
  margin: 0;
  padding: var(--space-4);
  border-top: var(--bw-thin) solid var(--border-default);
  display: grid;
  gap: var(--space-3);
  overflow-wrap: anywhere;
}

.delivery__detail dt {
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.delivery__detail dd,
.delivery__detail p {
  margin: 0;
}

@media (width <=1023px) {
  .delivery__columns {
    grid-template-columns: 1fr;
  }

  .delivery__overview {
    flex-wrap: wrap;
  }

  .delivery__summary {
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  }
}

.delivery__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.delivery__table th,
.delivery__table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}
</style>
